import { supabase } from "@/integrations/supabase/client";
import { calculateBookingPrice, type PricingRule, type SpecialPriceDate } from "./pricing";
import { formatPrice } from "@/utils/formatPrice";
import { startOfDay, endOfDay, format } from "date-fns";

interface CategoryJoin {
  slug: string;
  daily_minutes_limit: number | null;
  daily_limit_fallback_category: string | null;
  overflow_percent: number | null;
}

interface MembershipWithPlanAndCategory {
  plan_id: string;
  membership_plans: {
    category_id: string | null;
    customer_categories: CategoryJoin | null;
  } | null;
}

interface CustomerWithCategory {
  category_id: string | null;
  customer_categories: CategoryJoin | null;
}

interface BookingPlayerWithBooking {
  bookings: {
    duration_minutes: number;
    status: string;
    start_time: string;
  } | null;
}

interface SessionAttendanceWithSession {
  training_sessions: {
    duration_minutes: number;
    status: string;
    session_date: string;
  } | null;
}

/**
 * Resolve a player's customer category for an organization.
 * Priority: 1) Active membership plan -> 2) Customer record -> 3) "standard"
 */
export async function getPlayerCategory(
  userId: string,
  orgId: string
): Promise<{ slug: string; dailyMinutesLimit: number | null; fallbackCategory: string; overflowPercent: number }> {
  // 1. Check active membership -> plan -> category
  const { data: membership } = await supabase
    .from("memberships")
    .select("plan_id, membership_plans(category_id, customer_categories(slug, daily_minutes_limit, daily_limit_fallback_category, overflow_percent))")
    .eq("user_id", userId)
    .eq("organization_id", orgId)
    .eq("status", "active")
    .maybeSingle();

  const typedMembership = membership as unknown as MembershipWithPlanAndCategory | null;
  const memCat = typedMembership?.membership_plans?.customer_categories;
  if (memCat?.slug) {
    return {
      slug: memCat.slug,
      dailyMinutesLimit: memCat.daily_minutes_limit ?? null,
      fallbackCategory: memCat.daily_limit_fallback_category ?? "standard",
      overflowPercent: memCat.overflow_percent ?? 100,
    };
  }

  // 2. Check customers table
  const { data: customer } = await supabase
    .from("customers")
    .select("category_id, customer_categories(slug, daily_minutes_limit, daily_limit_fallback_category, overflow_percent)")
    .eq("user_id", userId)
    .eq("organization_id", orgId)
    .maybeSingle();

  const typedCustomer = customer as unknown as CustomerWithCategory | null;
  const custCat = typedCustomer?.customer_categories;
  if (custCat?.slug) {
    return {
      slug: custCat.slug,
      dailyMinutesLimit: custCat.daily_minutes_limit ?? null,
      fallbackCategory: custCat.daily_limit_fallback_category ?? "standard",
      overflowPercent: custCat.overflow_percent ?? 100,
    };
  }

  // 3. Fallback
  return { slug: "standard", dailyMinutesLimit: null, fallbackCategory: "standard", overflowPercent: 100 };
}

/**
 * Get how many court-minutes a player has used today across ALL activity types.
 */
export async function getUsedMinutesToday(
  userId: string,
  date: Date
): Promise<number> {
  const dayStart = startOfDay(date).toISOString();
  const dayEnd = endOfDay(date).toISOString();

  let total = 0;

  // 1. Bookings (via booking_players)
  const { data: bookings } = await supabase
    .from("booking_players")
    .select("bookings!inner(duration_minutes, status, start_time)")
    .eq("user_id", userId)
    .gte("bookings.start_time", dayStart)
    .lte("bookings.start_time", dayEnd)
    .in("bookings.status", ["confirmed", "completed"]);
  total += (bookings ?? []).reduce(
    (s: number, bp) => s + ((bp as unknown as BookingPlayerWithBooking).bookings?.duration_minutes ?? 0), 0
  );

  // 2. Training sessions (via session_attendance)
  try {
    const dateStr = format(date, "yyyy-MM-dd");
    const { data: sessions } = await supabase
      .from("session_attendance" as any)
      .select("training_sessions!inner(duration_minutes, status, session_date)")
      .eq("user_id", userId)
      .eq("training_sessions.session_date", dateStr)
      .in("training_sessions.status", ["scheduled", "completed"]);
    total += (sessions ?? []).reduce(
      (s: number, sa) => s + ((sa as unknown as SessionAttendanceWithSession).training_sessions?.duration_minutes ?? 0), 0
    );
  } catch { /* table may not exist */ }

  // 3. Private lessons
  try {
    const { data: lessons } = await supabase
      .from("private_lessons")
      .select("start_time, end_time")
      .eq("student_id", userId)
      .gte("start_time", dayStart)
      .lte("start_time", dayEnd)
      .in("status", ["confirmed", "completed"]);
    total += (lessons ?? []).reduce((s: number, l) => {
      const dur = (new Date(l.end_time).getTime() - new Date(l.start_time).getTime()) / 60000;
      return s + dur;
    }, 0);
  } catch { /* table may not exist */ }

  // 4. League matches are counted via bookings (they create booking records)

  return total;
}

// --- Universal Pricing -------------------------------------------------------

export interface ActivityPricing {
  /** Total court cost for the activity in ore (rabatterable) */
  courtCostTotal: number;
  /** Number of participants sharing the court cost */
  courtParticipants: number;
  /** Duration that counts against daily limit */
  courtDurationMinutes: number;
  /** Total service cost in ore (coach, admin -- never discounted) */
  serviceCostTotal: number;
  /** Number of participants sharing the service cost */
  serviceParticipants: number;
}

export interface CategoryConfig {
  slug: string;
  name: string;
  daily_minutes_limit: number | null;
  daily_limit_fallback_category: string | null;
  overflow_percent: number | null;
}

export interface PlayerPriceResult {
  userId: string;
  categorySlug: string;
  categoryName: string;

  // Court portion
  courtShare: number;
  discountedMinutes: number;
  standardMinutes: number;
  overflowRate: number;

  // Service portion
  serviceShare: number;

  // Totals
  totalShare: number;
  breakdown: string;

  // Daily tracking
  dailyMinutesUsedBefore: number;
  dailyMinutesUsedAfter: number;
  dailyMinutesLimit: number | null;
}

/**
 * Calculate a single player's price share using the universal formula:
 *   PLAYER_PRICE = court_portion(category, minutes) + service_portion
 *
 * Court portion is discountable via category pricing + daily limit + overflow_percent.
 * Service portion is NEVER discounted.
 */
export async function calculateUniversalPrice(
  userId: string,
  orgId: string,
  hallId: string,
  startTime: Date,
  activity: ActivityPricing,
  pricingRules: PricingRule[],
  specialDates: SpecialPriceDate[],
  categories: CategoryConfig[]
): Promise<PlayerPriceResult> {
  // 1. Resolve category
  const playerCat = await getPlayerCategory(userId, orgId);
  const catInfo = categories.find((c) => c.slug === playerCat.slug);
  const catName = catInfo?.name ?? playerCat.slug;
  const overflowRate = playerCat.overflowPercent;

  // 2. Get usage today (ALL activity types)
  const usedToday = await getUsedMinutesToday(userId, startTime);

  // 3. Calculate court portion
  const dailyLimit = playerCat.dailyMinutesLimit;
  let courtShare: number;
  let discountedMin: number;
  let standardMin: number;

  if (dailyLimit == null) {
    // No limit -- everything at category price
    const catPrice = calculateBookingPrice(
      pricingRules, hallId, startTime,
      activity.courtDurationMinutes, playerCat.slug, specialDates
    );
    courtShare = activity.courtParticipants > 0
      ? Math.round(catPrice.total / activity.courtParticipants)
      : 0;
    discountedMin = activity.courtDurationMinutes;
    standardMin = 0;
  } else {
    const remaining = Math.max(0, dailyLimit - usedToday);
    discountedMin = Math.min(remaining, activity.courtDurationMinutes);
    standardMin = activity.courtDurationMinutes - discountedMin;

    // Discounted portion at category price
    let discountedShare = 0;
    if (discountedMin > 0) {
      const catPrice = calculateBookingPrice(
        pricingRules, hallId, startTime,
        discountedMin, playerCat.slug, specialDates
      );
      discountedShare = activity.courtParticipants > 0
        ? Math.round((catPrice.perHour ?? 0) * discountedMin / 60 / activity.courtParticipants)
        : 0;
    }

    // Overflow portion at fallback price x overflow_percent
    let standardShare = 0;
    if (standardMin > 0) {
      const stdPrice = calculateBookingPrice(
        pricingRules, hallId, startTime,
        standardMin, playerCat.fallbackCategory, specialDates
      );
      standardShare = activity.courtParticipants > 0
        ? Math.round(
            (stdPrice.perHour ?? 0) * standardMin / 60
            / activity.courtParticipants
            * overflowRate / 100
          )
        : 0;
    }

    courtShare = discountedShare + standardShare;
  }

  // 4. Service portion (NEVER discounted)
  const serviceShare = activity.serviceParticipants > 0
    ? Math.round(activity.serviceCostTotal / activity.serviceParticipants)
    : 0;

  // 5. Total
  const totalShare = courtShare + serviceShare;

  // 6. Breakdown string
  const parts: string[] = [];
  if (discountedMin > 0) {
    parts.push(`${discountedMin} min bana (${catName})`);
  }
  if (standardMin > 0) {
    parts.push(`${standardMin} min bana (${overflowRate}% av standard)`);
  }
  if (courtShare > 0) {
    parts.push(`Banhyra: ${formatPrice(courtShare)}`);
  }
  if (serviceShare > 0) {
    parts.push(`Tjänst: ${formatPrice(serviceShare)}`);
  }
  const breakdown = parts.join(" + ") || "Gratis";

  return {
    userId,
    categorySlug: playerCat.slug,
    categoryName: catName,
    courtShare,
    discountedMinutes: discountedMin,
    standardMinutes: standardMin,
    overflowRate,
    serviceShare,
    totalShare,
    breakdown,
    dailyMinutesUsedBefore: usedToday,
    dailyMinutesUsedAfter: usedToday + activity.courtDurationMinutes,
    dailyMinutesLimit: dailyLimit,
  };
}

// --- Legacy wrapper for backward compatibility --------------------------------

/**
 * @deprecated Use calculateUniversalPrice instead
 */
export async function calculatePlayerPrice(
  userId: string,
  orgId: string,
  hallId: string,
  startTime: Date,
  durationMinutes: number,
  totalPlayers: number,
  pricingRules: PricingRule[],
  specialDates: SpecialPriceDate[],
  categories: { slug: string; name: string; daily_minutes_limit: number | null; daily_limit_fallback_category: string | null }[]
): Promise<PlayerPriceResult> {
  // Calculate total court cost at standard rate for the activity
  const stdPrice = calculateBookingPrice(
    pricingRules, hallId, startTime, durationMinutes, "standard", specialDates
  );

  return calculateUniversalPrice(
    userId, orgId, hallId, startTime,
    {
      courtCostTotal: stdPrice.total,
      courtParticipants: totalPlayers,
      courtDurationMinutes: durationMinutes,
      serviceCostTotal: 0,
      serviceParticipants: totalPlayers,
    },
    pricingRules, specialDates,
    categories.map((c) => ({
      ...c,
      overflow_percent: 100,
    }))
  );
}
